import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';

import LocalPhoneIcon from '@mui/icons-material/LocalPhone';

import { Navigate, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';

import { alpha } from '@mui/material/styles';

import PageHeader from '../components/PageHeader';

export default function Users({setNavbarState, navbarState, window}) {
  const [users, setUsers] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = React.useState(null);
  const [userModalOpen, setUserModalOpen] = React.useState(false);

  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 20;

  const unitType = localStorage.getItem('unit_type');

  // filter the users based on the search value
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchValue.toLowerCase())
    || user.lastname.toLowerCase().includes(searchValue.toLowerCase())
    || user.phone.toLowerCase().includes(searchValue.toLowerCase())
    || user.unit.toLowerCase().includes(searchValue.toLowerCase())
  );

  // function to fetch all users
  const fetchUsers = () => {
    setLoading(true);
    setOffset(PAGE_SIZE);
    apiClient.get(`users/allusers?limit=${PAGE_SIZE}&offset=0`, {}, { action: 'fetch_directory' })
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data || {};
          const items = data.items || [];
          const sortedUsers = items.sort((a, b) => a.name.localeCompare(b.name));
          setUsers(sortedUsers);
          setHasMore(data.hasMore || false);
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching directory:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' })
        } else if (error.response.data.code === 403) {
          localStorage.setItem('appear_in_directory', false)
          location.href = '/'
        }
      })
  }

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const currentOffset = offset;
    apiClient.get(`users/allusers?limit=${PAGE_SIZE}&offset=${currentOffset}`, {}, { action: 'fetch_directory_more' })
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data || {};
          const newItems = data.items || [];
          setUsers(prev => [...prev, ...newItems].sort((a, b) => a.name.localeCompare(b.name)));
          setOffset(currentOffset + PAGE_SIZE);
          setHasMore(data.hasMore || false);
        }
        setLoadingMore(false);
      })
      .catch((error) => {
        console.error('Error loading more users:', error);
        setLoadingMore(false);
      });
  };

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    }, { rootMargin: '100px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, offset]);

  // Get the users when the component is rendered
  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    fetchUsers()
  }, [])

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const getUnitLabel = React.useCallback((userUnit) => {
    return userUnit === 'Administracion' || userUnit === 'Recepcion' ? 'Unidad' : unitType;
  }, [unitType]);

  const openUserModal = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const closeUserModal = () => {
    setUserModalOpen(false);
    setSelectedUser(null);
  };
  
  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 2,
            pt: 1.75,
            pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottomLeftRadius: '18px',
            borderBottomRightRadius: '18px',
            overflow: 'hidden',
            boxShadow: (theme) => (theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2,21,69,0.08)'),
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <PageHeader
              title="Directorio"
              icon="notebook"
              onBack={() => navigate(-1)}
            />
          </Box>
          <Paper
            elevation={0}
            sx={(theme) => ({
              borderRadius: '14px',
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.common.black, 0.02),
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 10px rgba(0,0,0,0.35)'
                : '0 10px 25px rgba(2,21,69,0.06)',
            })}
          >
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ position: 'absolute', left: 12, top: 0, bottom: 0, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </Box>
              <InputBase
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Buscar por nombre, unidad o teléfono…"
                inputProps={{ 'aria-label': 'buscar en directorio' }}
                sx={{
                  width: '100%',
                  pl: 5,
                  pr: 2,
                  py: 1.25,
                  fontSize: 14,
                }}
              />
            </Box>
          </Paper>
        </Box>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Box sx={{ px: 3, pt: 1.75 }}>
          {!loading && (
            <Stack spacing={1.25} sx={{ pb: 2 }}>
              {filteredUsers.map((user) => (
                <Paper
                  key={user.username}
                  elevation={0}
                  onClick={() => openUserModal(user)}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : alpha(theme.palette.common.black, 0.02),
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 2px 10px rgba(0,0,0,0.08)'
                        : '0 10px 25px rgba(2,21,69,0.02)',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 6px 18px rgba(0,0,0,0.12)'
                          : '0 16px 34px rgba(2,21,69,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary' }} noWrap>
                        {user.name} {user.lastname}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }} noWrap>
                        {getUnitLabel(user.unit)}: {user.unit}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <IconButton
                        component="a"
                        href={`tel:${user.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: 'text.secondary' }}
                        aria-label="Llamar"
                        size="large"
                      >
                        <LocalPhoneIcon />
                      </IconButton>
                      <IconButton
                        component="a"
                        href={`https://wa.me/${user.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: 'text.secondary' }}
                        aria-label="WhatsApp"
                        size="large"
                      >
                        <WhatsAppIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
              {hasMore && (
                <div ref={sentinelRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {loadingMore && <CircularProgress size={24} />}
                </div>
              )}
              {!hasMore && filteredUsers.length > 0 && (
                <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>
                  No hay más residentes
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={userModalOpen}
        onClose={closeUserModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedUser ? `${selectedUser.name} ${selectedUser.lastname}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body1" sx={{ mb: 1.25 }}>
              {selectedUser ? `${getUnitLabel(selectedUser.unit)}: ${selectedUser.unit}` : ''}
            </Typography>
            <Typography variant="body1">
              {selectedUser ? `Teléfono: ${selectedUser.phone}` : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Stack direction="column" spacing={1.5} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<WhatsAppIcon />}
              href={selectedUser ? `https://wa.me/${selectedUser.phone}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              disabled={!selectedUser}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<LocalPhoneIcon />}
              href={selectedUser ? `tel:${selectedUser.phone}` : undefined}
              fullWidth
              disabled={!selectedUser}
            >
              Llamar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Container>
  );
}